import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Input, Select, Button, message, Modal, Spin, Typography, Divider, Alert, Breadcrumb, App } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../utils/api';
import { theme } from '../../theme';
import { ExclamationCircleOutlined, HomeOutlined, UserOutlined as UserOutlinedIcon, EditOutlined } from '@ant-design/icons';
import { PageTitle, StyledCard } from '../common';

const { Text } = Typography;

const { Option } = Select;

// Interfaces para los datos
interface Provincia {
  id: number;
  provincia: string;
}

interface Localidad {
  id: number;
  localidad: string;
  departamento: string;
  departamento_id: number;
}

interface TipoDocumento {
  id: number;
  descripcion: string;
}

interface CategoriaIva {
  id: number;
  civa: string;
}

interface Actividad {
  id: number;
  actividad: string;
}

interface PadronData {
  id: number;
  nc: string;                    // Número de cliente
  denominacion: string;          // Nombre en el backend
  direccion: string;
  provincia_id: number;          // ID de provincia
  provincia: string;             // Nombre de provincia
  localidad_id: number;          // ID de localidad
  localidad: string;             // Nombre de localidad
  departamento_id: number;       // ID de departamento
  departamento: string;          // Nombre de departamento
  tipo_documento_id: number;     // ID de tipo documento
  tipo_documento: string;        // Nombre de tipo documento
  nro_documento: string;         // Número de documento
  telefono: string;              // Teléfono
  email: string;                 // Email
  categoria_iva_id: number;      // ID de categoría IVA
  categoria_iva: string;         // Nombre de categoría IVA
  actividad_id: number;          // ID de actividad
  actividad: string;             // Nombre de actividad
  estado: string;                // Estado (Activo/Baja)
}

interface FormValues {
  denominacion: string;          // Razón social
  direccion: string;
  provincia_id: number | undefined;
  localidad_id: number | undefined;
  departamento: string;
  departamento_id: number | undefined;
  tipo_documento_id: number | undefined;
  nro_documento: string;
  telefono: string;
  email: string;
  categoria_iva_id: number | undefined;
  actividad_id: number | undefined;
}

// Agregar el id como prop del componente
interface EditarPadronProps {
  id: number;
  open?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
  asModal?: boolean;
}

// Componente EditarPadron
const EditarPadronComponent: React.FC<EditarPadronProps> = ({ id, open, onCancel, onSuccess, asModal = false }) => {
  const [form] = Form.useForm<FormValues>();
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [categoriasIva, setCategoriasIva] = useState<CategoriaIva[]>([]);
  const [actividades, setActividades] = useState<Actividad[]>([]);
  const [departamento, setDepartamento] = useState('');
  const [userData, setUserData] = useState<any>(null);
  const [padronData, setPadronData] = useState<PadronData | null>(null);
  const [documentoDuplicado, setDocumentoDuplicado] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialValuesRef = useRef<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingCatalogos(true);
        const token = await getAccessTokenSilently();

        // 1. Cargar catálogos básicos
        console.log('1. Cargando catálogos básicos...');
        const [provinciasRes, tiposDocRes, tiposRespRes, actividadesRes] = await Promise.all([
          apiClient.get<Provincia[]>(`${process.env.REACT_APP_COMMON_URL}/listar_provincias`, { token }),
          apiClient.get<TipoDocumento[]>(`${process.env.REACT_APP_COMMON_URL}/listar_tipodocumento`, { token }),
          apiClient.get<CategoriaIva[]>(`${process.env.REACT_APP_COMMON_URL}/listar_tiporesponsable`, { token }),
          apiClient.get<Actividad[]>(`${process.env.REACT_APP_COMMON_URL}/listar_actividades`, { token })
        ]);
        setProvincias(provinciasRes.data);
        setTiposDocumento(tiposDocRes.data);
        setCategoriasIva(tiposRespRes.data);
        setActividades(actividadesRes.data);
        console.log('Catálogos básicos cargados');

        // 2. Obtener datos del padrón
        console.log('2. Obteniendo datos del padrón...');
        const padronResponse = await apiClient.get(
          `${process.env.REACT_APP_PADRON_URL}/obtener_padron/${user?.id_empresa}/${id}`,
          { token }
        );
        const padronData: PadronData = Array.isArray(padronResponse.data)
          ? padronResponse.data[0]
          : padronResponse.data;
        console.log('Datos recibidos del padrón:', padronData);

        // 3. Cargar localidades de la provincia del padrón
        let localidadesData: Localidad[] = [];
        if (padronData.provincia_id) {
          console.log('3. Cargando localidades de la provincia:', padronData.provincia_id);
          const localidadesRes = await apiClient.get<Localidad[]>(
            `${process.env.REACT_APP_COMMON_URL}/listar_localidades/${padronData.provincia_id}`,
            { token }
          );
          localidadesData = localidadesRes.data;
          setLocalidades(localidadesData);
          console.log('Localidades cargadas:', localidadesData);
        }

        // 4. Setear valores en el formulario
        const formData = {
          denominacion: padronData.denominacion,
          direccion: padronData.direccion,
          provincia_id: padronData.provincia_id,
          localidad_id: padronData.localidad_id,
          departamento: padronData.departamento,
          departamento_id: padronData.departamento_id,
          tipo_documento_id: padronData.tipo_documento_id,
          nro_documento: padronData.nro_documento,
          telefono: padronData.telefono,
          email: padronData.email,
          categoria_iva_id: padronData.categoria_iva_id,
          actividad_id: padronData.actividad_id
        };
        form.setFieldsValue(formData);
        setDepartamento(padronData.departamento);
        setPadronData(padronData);
        console.log('Valores establecidos en el formulario:', formData);
        console.log('Proceso completado exitosamente');
      } catch (error) {
        message.error('Error al cargar los datos. Por favor, recargue la página.');
        console.error(error);
      } finally {
        setLoadingCatalogos(false);
      }
    };

    if (user?.id_empresa && id) {
      // Si es modal, solo cargar cuando el modal esté abierto
      if (asModal && !open) {
        return;
      }
      fetchData();
    }
  }, [user, id, getAccessTokenSilently, form, asModal, open]);

  useEffect(() => {
    if (user) {
      setUserData({
        id_empresa: user?.id_empresa,
        id_sucursal: user?.id_sucursal
      });
    }
  }, [user]);

  // Inicializar valores iniciales cuando se cargan los datos
  useEffect(() => {
    if (padronData && !loadingCatalogos) {
      const initialValues = form.getFieldsValue();
      initialValuesRef.current = JSON.parse(JSON.stringify(initialValues));
    }
  }, [padronData, loadingCatalogos, form]);

  // Detectar cambios sin guardar
  useEffect(() => {
    if (!initialValuesRef.current) return;
    
    const checkChanges = () => {
      const currentValues = form.getFieldsValue();
      const hasChanges = JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current);
      setHasUnsavedChanges(hasChanges);
    };

    const timer = setInterval(checkChanges, 500);
    return () => clearInterval(timer);
  }, [form, padronData]);

  // Confirmar antes de salir si hay cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Validar documento duplicado (excluyendo el registro actual)
  const validarDocumentoDuplicado = async (nroDocumento: string, tipoDocumentoId: number) => {
    // Excepciones: permitir 00000 y 99999
    const documentoNormalizado = nroDocumento.replace(/\D/g, '');
    if (documentoNormalizado === '00000000000' || documentoNormalizado === '99999999999') {
      setDocumentoDuplicado(false);
      return Promise.resolve();
    }

    try {
      const token = await getAccessTokenSilently();
      if (!userData?.id_empresa) return Promise.resolve();

      // Buscar si existe el documento (excluyendo el registro actual)
      const response = await apiClient.get(
        `${process.env.REACT_APP_PADRON_URL}/buscar_padron/${userData.id_empresa}/`,
        { token }
      );

      const existeDuplicado = response.data.some((record: any) =>
        record.id !== id &&
        record.ndocumento === documentoNormalizado
      );

      if (existeDuplicado) {
        setDocumentoDuplicado(true);
        return Promise.reject(new Error('Ya existe otro registro con este documento'));
      } else {
        setDocumentoDuplicado(false);
        return Promise.resolve();
      }
    } catch (error) {
      console.error('Error al validar documento:', error);
      // Si hay error en la validación, permitir continuar
      return Promise.resolve();
    }
  };

  const handleProvinciaChange = async (provinciaId: number) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await apiClient.get<Localidad[]>(
        `${process.env.REACT_APP_COMMON_URL}/listar_localidades/${provinciaId}`,
        { token }
      );
      setLocalidades(response.data);
      
      // Solo limpiar si no es la carga inicial
      if (!loadingCatalogos) {
        form.setFieldsValue({
          localidad_id: undefined,
          departamento: '',
          departamento_id: undefined
        });
      }
    } catch (error) {
      console.error('Error al cargar localidades:', error);
      message.error('Error al cargar localidades');
    }
  };

  const handleLocalidadChange = (localidadId: number) => {
    const localidad = localidades.find(l => l.id === localidadId);
    if (localidad) {
      console.log('Localidad seleccionada:', localidad);
      setDepartamento(localidad.departamento);
      form.setFieldsValue({
        departamento: localidad.departamento,
        departamento_id: localidad.departamento_id
      });
      console.log('Departamento actualizado:', localidad.departamento);
    } else {
      setDepartamento('');
      form.setFieldsValue({
        departamento: '',
        departamento_id: undefined
      });
      console.log('Departamento limpiado');
    }
  };

  const validateForm = (values: FormValues) => {
    if (!values.provincia_id) {
      message.error('Debe seleccionar una provincia');
      return false;
    }
    if (!values.localidad_id) {
      message.error('Debe seleccionar una localidad');
      return false;
    }
    if (!values.tipo_documento_id) {
      message.error('Debe seleccionar un tipo de documento');
      return false;
    }
    if (!values.categoria_iva_id) {
      message.error('Debe seleccionar una categoría de IVA');
      return false;
    }
    if (!values.actividad_id) {
      message.error('Debe seleccionar una actividad');
      return false;
    }
    return true;
  };

  // Calcular progreso del formulario
  const formValues = Form.useWatch([], form);
  const formProgress = useMemo(() => {
    if (!formValues) return 0;
    
    const totalFields = 11; // Total de campos requeridos
    let completedFields = 0;

    if (formValues.denominacion) completedFields++;
    if (formValues.direccion) completedFields++;
    if (formValues.provincia_id) completedFields++;
    if (formValues.localidad_id) completedFields++;
    if (formValues.tipo_documento_id) completedFields++;
    if (formValues.nro_documento && formValues.nro_documento.length === 11) completedFields++;
    if (formValues.telefono) completedFields++;
    if (formValues.email) completedFields++;
    if (formValues.categoria_iva_id) completedFields++;
    if (formValues.actividad_id) completedFields++;
    if (formValues.departamento_id) completedFields++;

    return Math.round((completedFields / totalFields) * 100);
  }, [formValues]);

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: '¿Descartar cambios?',
        icon: <ExclamationCircleOutlined />,
        content: 'Tiene cambios sin guardar. ¿Está seguro de que desea salir?',
        okText: 'Sí, descartar',
        cancelText: 'Cancelar',
        okType: 'danger',
        onOk: () => {
          setHasUnsavedChanges(false);
          if (asModal && onCancel) {
            form.setFieldsValue(initialValuesRef.current);
            onCancel();
          } else {
            navigate('/listado_padron');
          }
        }
      });
    } else {
      if (asModal && onCancel) {
        form.setFieldsValue(initialValuesRef.current);
        onCancel();
      } else {
        navigate('/listado_padron');
      }
    }
  };

  const onFinish = async (values: FormValues) => {
    if (!validateForm(values)) return;
    try {
      // Validar documento duplicado antes de guardar
      const documentoNormalizado = values.nro_documento.replace(/\D/g, '');
      if (documentoNormalizado !== '00000000000' && documentoNormalizado !== '99999999999') {
        try {
          await validarDocumentoDuplicado(documentoNormalizado, values.tipo_documento_id!);
        } catch (error: any) {
          if (error.message.includes('Ya existe')) {
            message.warning({
              content: 'Ya existe otro registro con este documento. ¿Desea continuar de todas formas?',
              duration: 5,
            });
            // Permitir continuar después de mostrar advertencia
          }
        }
      }

      // Mostrar confirmación antes de actualizar
      const confirmed = await new Promise(resolve => {
        Modal.confirm({
          title: '¿Confirma la actualización?',
          content: 'Esta acción actualizará los datos del padrón',
          okText: 'Sí',
          cancelText: 'No',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

      if (!confirmed) return;

      // Activar loading
      setLoading(true);
      message.loading('Procesando actualización...', 0);

      if (!userData?.id_empresa) {
        message.error('No se encontraron datos de empresa');
        return;
      }

      const token = await getAccessTokenSilently();
      
      // Preparar los datos para enviar al backend
      const dataToSend = {
        denominacion: values.denominacion,
        direccion: values.direccion,
        provincia_id: values.provincia_id,
        localidad_id: values.localidad_id,
        departamento_id: values.departamento_id,
        tipo_documento_id: values.tipo_documento_id,
        ndocumento: values.nro_documento,
        nro_telefono: values.telefono,
        email: values.email,
        categoria_iva_id: values.categoria_iva_id,
        actividad_id: values.actividad_id
      };

      const response = await apiClient.post(
        `${process.env.REACT_APP_PADRON_URL}/actualizar_padron/${userData.id_empresa}/${id}`,
        dataToSend,
        { token }
      );
      
      // Ocultar mensaje de loading y mostrar éxito
      message.destroy();
      message.success({
        content: 'Registro actualizado exitosamente',
        duration: 3,
      });
      setHasUnsavedChanges(false);
      initialValuesRef.current = JSON.parse(JSON.stringify(form.getFieldsValue()));
      
      // Si es modal, cerrar y refrescar listado. Si es página, navegar.
      if (asModal && onSuccess) {
        onSuccess();
      } else {
        setTimeout(() => {
          navigate('/listado_padron');
        }, 1000);
      }

    } catch (error: any) {
      // Ocultar mensaje de loading y mostrar error
      message.destroy();
      console.error('Error completo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al actualizar el registro';
      message.error({
        content: errorMessage,
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };


  // Contenido del formulario
  const formContent = (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={() => setHasUnsavedChanges(true)}
        style={{ 
          maxWidth: '100%',
          padding: asModal ? '16px' : '16px'
        }}
        className="padron-form"
      >
        <style>{`
          .padron-form .ant-form-item-label > label {
            color: #374151 !important;
            font-weight: 500 !important;
            font-size: 13px !important;
            line-height: 1.4 !important;
          }
          .padron-form .ant-form-item {
            margin-bottom: 0 !important;
          }
          .padron-form .ant-form-item-required::before {
            color: ${theme.colors.error} !important;
          }
          .padron-form .ant-input,
          .padron-form .ant-select-selector,
          .padron-form .ant-input-number {
            border-color: #d1d5db !important;
            border-radius: 6px !important;
          }
          .padron-form .ant-input:focus,
          .padron-form .ant-select-selector:focus,
          .padron-form .ant-input-number:focus {
            border-color: ${theme.colors.primary} !important;
            box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
          }
        `}</style>

        {/* Alerta de cambios sin guardar */}
        {hasUnsavedChanges && (
          <Alert
            message="Tiene cambios sin guardar"
            type="warning"
            showIcon
            closable
            style={{ marginBottom: '20px' }}
            action={
              <Button size="small" onClick={() => {
                form.setFieldsValue(initialValuesRef.current);
                setHasUnsavedChanges(false);
              }}>
                Descartar
              </Button>
            }
          />
        )}

        {/* Alerta de documento duplicado */}
        {documentoDuplicado && (
          <Alert
            message="Advertencia: Ya existe otro registro con este documento"
            description="Se detectó un documento duplicado en otro registro. Puede continuar si está seguro de que es correcto."
            type="warning"
            showIcon
            closable
            onClose={() => setDocumentoDuplicado(false)}
            style={{ marginBottom: '12px' }}
          />
        )}
        
        {/* Layout compacto: todos los campos en una sola vista */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          {/* Razón Social */}
          <Form.Item
            name="denominacion"
            label="Razón Social"
            rules={[{ required: true, message: 'Ingrese la razón social' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input placeholder="Ej: Empresa S.A., Juan Pérez, etc." size="small" />
          </Form.Item>

          {/* Dirección */}
          <Form.Item
            name="direccion"
            label="Dirección"
            rules={[{ required: true, message: 'Ingrese la dirección' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input placeholder="Ej: Av. Libertador 1234, Piso 5, Dpto. A" size="small" />
          </Form.Item>

          {/* Provincia */}
          <Form.Item
            name="provincia_id"
            label="Provincia"
            rules={[{ required: true, message: 'Seleccione la provincia' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select 
              onChange={handleProvinciaChange}
              placeholder="Seleccione provincia"
              showSearch
              optionFilterProp="children"
              size="small"
            >
              {provincias.map(prov => (
                <Option key={prov.id} value={prov.id}>{prov.provincia}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Localidad */}
          <Form.Item
            name="localidad_id"
            label="Localidad"
            rules={[{ required: true, message: 'Seleccione la localidad' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select 
              onChange={handleLocalidadChange}
              placeholder="Seleccione localidad"
              showSearch
              optionFilterProp="children"
              disabled={!form.getFieldValue('provincia_id')}
              size="small"
            >
              {localidades.map(loc => (
                <Option key={loc.id} value={loc.id}>{loc.localidad}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Departamento */}
          <Form.Item 
            label="Departamento"
            name="departamento"
            style={{ marginBottom: '16px' }}
          >
            <Input 
              disabled 
              placeholder="Se completa automáticamente"
              size="small"
            />
          </Form.Item>
          <Form.Item name="departamento_id" hidden>
            <Input type="hidden" />
          </Form.Item>

          {/* Tipo Documento */}
          <Form.Item
            name="tipo_documento_id"
            label="Tipo Documento"
            rules={[{ required: true, message: 'Seleccione el tipo de documento' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select placeholder="Seleccione tipo de documento" size="small">
              {tiposDocumento.map(tipo => (
                <Option key={tipo.id} value={tipo.id}>{tipo.descripcion}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Nro. Documento */}
          <Form.Item
            name="nro_documento"
            label="Nro. Documento"
            rules={[
              { required: true, message: 'Ingrese el número de documento' },
              {
                validator: async (_, value) => {
                  if (!value) return Promise.resolve();
                  const documentoNormalizado = value.replace(/\D/g, '');
                  const tipoDocId = form.getFieldValue('tipo_documento_id');
                  const tipoDoc = tiposDocumento.find(t => t.id === tipoDocId);
                  const tipoDescripcion = (tipoDoc?.descripcion || '').toUpperCase();
                  const esCuitCuil = tipoDescripcion.includes('CUIT') || tipoDescripcion.includes('CUIL');
                  const esDni = tipoDescripcion.includes('DNI');

                  if (esCuitCuil) {
                    if (documentoNormalizado.length !== 11) {
                      return Promise.reject(new Error(`El CUIT/CUIL debe tener exactamente 11 dígitos (tiene ${documentoNormalizado.length})`));
                    }
                  } else if (esDni) {
                    if (documentoNormalizado.length < 8) {
                      return Promise.reject(new Error(`El DNI debe tener al menos 8 dígitos (tiene ${documentoNormalizado.length})`));
                    }
                    if (documentoNormalizado.length > 11) {
                      return Promise.reject(new Error('El DNI no puede tener más de 11 dígitos'));
                    }
                  } else {
                    if (documentoNormalizado.length > 11) {
                      return Promise.reject(new Error('El número de documento no puede tener más de 11 dígitos'));
                    }
                  }

                  if (tipoDocId) {
                    await validarDocumentoDuplicado(documentoNormalizado, tipoDocId);
                  }
                  return Promise.resolve();
                }
              }
            ]}
            extra={
              <Text type="secondary" style={{ fontSize: '11px', lineHeight: '1.2' }}>
                CUIT/CUIL: 11 dígitos. DNI: 8 a 11 dígitos. Se permiten 00000000000 y 99999999999.
              </Text>
            }
            style={{ marginBottom: '16px' }}
          >
            <Input
              maxLength={11}
              placeholder="Ej: 20123456789"
              onKeyPress={(e) => {
                const isNumber = /\d/.test(e.key);
                if (!isNumber) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                form.setFieldValue('nro_documento', value);
              }}
              size="small"
            />
          </Form.Item>

          {/* Categoría IVA */}
          <Form.Item
            name="categoria_iva_id"
            label="Categoría IVA"
            rules={[{ required: true, message: 'Seleccione la categoría de IVA' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select placeholder="Seleccione categoría IVA" size="small">
              {categoriasIva.map(cat => (
                <Option key={cat.id} value={cat.id}>{cat.civa}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Teléfono */}
          <Form.Item
            name="telefono"
            label="Nro. de Teléfono"
            rules={[{ required: true, message: 'Ingrese el número de teléfono' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input placeholder="Ej: (011) 1234-5678" size="small" />
          </Form.Item>

          {/* Email */}
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Ingrese el email' },
              { type: 'email', message: 'Ingrese un email válido' }
            ]}
            style={{ marginBottom: '16px' }}
          >
            <Input type="email" placeholder="Ej: contacto@empresa.com.ar" size="small" />
          </Form.Item>

          {/* Actividad */}
          <Form.Item
            name="actividad_id"
            label="Actividad"
            rules={[{ required: true, message: 'Seleccione la actividad' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select placeholder="Seleccione actividad" size="small">
              {actividades.map(act => (
                <Option key={act.id} value={act.id}>{act.actividad}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        {/* Botones de acción */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: `1px solid ${theme.colors.borderLight}`
        }}>
          <Button
            onClick={handleCancel}
            size="small"
            style={{
              fontWeight: 500,
              minWidth: '120px'
            }}
          >
            Cancelar
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="small"
            disabled={loading}
            style={{
              backgroundColor: theme.colors.primary,
              borderColor: theme.colors.primary,
              fontWeight: 500,
              minWidth: '120px'
            }}
          >
            {loading ? 'Actualizando...' : 'Actualizar'}
          </Button>
        </div>

      </Form>
    </>
  );

  // Si es modal, retornar el contenido del formulario directamente
  if (asModal) {
    return (
      <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '0' }}>
        {loadingCatalogos ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '300px',
            gap: '16px'
          }}>
            <Spin size="large" />
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ fontSize: '16px', color: theme.colors.textPrimary, display: 'block', marginBottom: '8px' }}>
                Cargando datos del registro...
              </Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Obteniendo información del cliente y catálogos necesarios
              </Text>
            </div>
          </div>
        ) : (
          <>
            {/* Indicador de progreso para modal */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: '8px',
              marginBottom: '16px',
              border: `1px solid ${theme.colors.borderLight}`
            }}>
              <Text strong style={{ fontSize: '14px', color: theme.colors.primaryDark }}>
                Editar Registro {padronData?.nc && `- N° ${padronData.nc}`}
              </Text>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>Progreso:</Text>
                <div style={{
                  width: '100px',
                  height: '6px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${formProgress}%`,
                    height: '100%',
                    backgroundColor: formProgress === 100 ? '#52c41a' : theme.colors.primary,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <Text strong style={{ fontSize: '12px', color: theme.colors.textPrimary, minWidth: '30px' }}>
                  {formProgress}%
                </Text>
              </div>
            </div>
            {formContent}
          </>
        )}
      </div>
    );
  }

  // Mostrar loading mientras se cargan los catálogos (modo página)
  if (loadingCatalogos) {
    return (
      <App>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          gap: '16px'
        }}>
          <Spin size="large" />
          <div style={{ textAlign: 'center' }}>
            <Text strong style={{ fontSize: '16px', color: theme.colors.textPrimary, display: 'block', marginBottom: '8px' }}>
              Cargando datos del registro...
            </Text>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Obteniendo información del cliente y catálogos necesarios
            </Text>
          </div>
        </div>
      </App>
    );
  }

  // Modo página (comportamiento original)
  return (
    <App>
      <div style={{ padding: '0' }}>
        {/* Breadcrumb */}
        <Breadcrumb
          style={{ marginBottom: '16px' }}
          items={[
            {
              href: '/listado_padron',
              title: (
                <span>
                  <HomeOutlined />
                  <span style={{ marginLeft: '8px' }}>Padrón</span>
                </span>
              ),
            },
            {
              title: (
                <span>
                  <EditOutlined />
                  <span style={{ marginLeft: '8px' }}>
                    Editar Registro {padronData?.nc && `- N° ${padronData.nc}`}
                  </span>
                </span>
              ),
            },
          ]}
        />

        {/* Título de la página */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
          <PageTitle style={{ marginBottom: 0, fontSize: '24px' }}>
            Editar Registro {padronData?.nc && `- N° ${padronData.nc}`}
          </PageTitle>
          
          {/* Indicador de progreso */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 16px',
            backgroundColor: theme.colors.backgroundGray,
            borderRadius: '8px',
            border: `1px solid ${theme.colors.borderLight}`
          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Progreso:</Text>
            <div style={{
              width: '120px',
              height: '8px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${formProgress}%`,
                height: '100%',
                backgroundColor: formProgress === 100 ? '#52c41a' : theme.colors.primary,
                transition: 'width 0.3s ease'
              }} />
            </div>
            <Text strong style={{ fontSize: '12px', color: theme.colors.textPrimary, minWidth: '35px' }}>
              {formProgress}%
            </Text>
          </div>
        </div>

      <StyledCard
        title="Información del Registro"
        style={{ 
          maxWidth: '1200px',
          position: 'relative'
        }}
        bodyStyle={{ padding: '12px' }}
      >
        {formContent}
      </StyledCard>
    </div>
    </App>
  );
};

// Página contenedora
const EditarPadronPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return <div>Error: No se proporcionó ID</div>;
  }

  return (
    <div style={{ padding: '20px', textAlign: 'center', alignItems: 'center', justifyContent: 'center' }}>
      <EditarPadronComponent id={parseInt(id)} />
    </div>
  );
};

export { EditarPadronComponent as EditarPadron };
export default EditarPadronPage;
